import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const MarketDetailsForm = ({ marketData, updateMarketData, categories, validationErrors }) => {
  const [showTagInput, setShowTagInput] = useState(false);
  const [newTag, setNewTag] = useState('');

  const handleTitleChange = (e) => {
    const value = e?.target?.value;
    updateMarketData('title', value);
  };

  const handleDescriptionChange = (e) => {
    const value = e?.target?.value;
    updateMarketData('description', value);
  };

  const handleAddTag = () => {
    if (newTag?.trim() && marketData?.tags?.length < 5 && !marketData?.tags?.includes(newTag?.trim())) {
      updateMarketData('tags', [...marketData?.tags, newTag?.trim()]);
      setNewTag('');
      setShowTagInput(false);
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    updateMarketData('tags', marketData?.tags?.filter(tag => tag !== tagToRemove));
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter') {
      e?.preventDefault();
      handleAddTag();
    }
  };

  const titleSuggestions = [
    "Will Bitcoin reach $100,000 by end of 2024?",
    "Will the next iPhone have a foldable screen?",
    "Will SpaceX launch humans to Mars by 2028?",
    "Will a major tech company acquire a social media platform in 2024?",
    "Will renewable energy exceed 50% of US power generation by 2025?"
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-heading-semibold text-text-primary mb-6">Market Details</h2>
        <p className="text-text-secondary mb-8">
          Define your prediction market question and provide context for traders.
        </p>
      </div>

      {/* Market Question */}
      <div>
        <label className="block text-sm font-body-semibold text-text-primary mb-2">
          Market Question *
        </label>
        <div className="relative">
          <textarea
            value={marketData?.title}
            onChange={handleTitleChange}
            placeholder="Ask a clear, specific question about a future event..."
            rows={3}
            maxLength={200}
            className={`w-full px-4 py-3 border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary bg-background text-text-primary resize-none ${
              validationErrors?.title ? 'border-error' : 'border-border'
            }`}
          />
          <div className="absolute bottom-3 right-3 text-xs text-text-secondary">
            {marketData?.title?.length || 0}/200
          </div>
        </div>
        {validationErrors?.title && (
          <p className="mt-2 text-sm text-error flex items-center">
            <Icon name="AlertCircle" size={16} className="mr-1" />
            {validationErrors?.title}
          </p>
        )}
        
        {/* Title Suggestions */}
        {marketData?.title?.length < 10 && (
          <div className="mt-3">
            <p className="text-xs text-text-secondary mb-2">Example questions:</p>
            <div className="space-y-1">
              {titleSuggestions?.slice(0, 3)?.map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => updateMarketData('title', suggestion)}
                  className="block text-xs text-primary hover:text-primary-300 hover:underline transition-colors duration-trading-fast"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-body-semibold text-text-primary mb-2">
          Detailed Description *
        </label>
        <div className="relative">
          <textarea
            value={marketData?.description}
            onChange={handleDescriptionChange}
            placeholder="Provide detailed context, sources, and clarification for your market question..."
            rows={6}
            maxLength={2000}
            className={`w-full px-4 py-3 border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary bg-background text-text-primary resize-none ${
              validationErrors?.description ? 'border-error' : 'border-border'
            }`}
          />
          <div className="absolute bottom-3 right-3 text-xs text-text-secondary">
            {marketData?.description?.length || 0}/2000
          </div>
        </div>
        {validationErrors?.description && (
          <p className="mt-2 text-sm text-error flex items-center">
            <Icon name="AlertCircle" size={16} className="mr-1" />
            {validationErrors?.description}
          </p>
        )}
        <p className="mt-2 text-xs text-text-secondary">
          Include relevant background information, data sources, and criteria for resolution.
        </p>
      </div>

      {/* Category Selection */}
      <div>
        <label className="block text-sm font-body-semibold text-text-primary mb-2">
          Category *
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {categories?.map((category) => (
            <button
              key={category?.id}
              onClick={() => updateMarketData('category', category?.id)}
              className={`flex items-center space-x-2 p-3 rounded-trading border transition-all duration-trading-fast ${
                marketData?.category === category?.id
                  ? 'bg-primary-900 border-primary-800 text-primary-300 glow-primary' :'bg-surface-hover border-border text-text-secondary hover:text-text-primary hover:border-border-hover'
              }`}
            >
              <Icon name={category?.icon} size={16} />
              <span className="text-sm font-body-medium">{category?.label}</span>
            </button>
          ))}
        </div>
        {validationErrors?.category && (
          <p className="mt-2 text-sm text-error flex items-center">
            <Icon name="AlertCircle" size={16} className="mr-1" />
            {validationErrors?.category}
          </p>
        )}
      </div>

      {/* Tags */}
      <div>
        <label className="block text-sm font-body-semibold text-text-primary mb-2">
          Tags (Optional)
        </label>
        <div className="flex flex-wrap gap-2 mb-3">
          {marketData?.tags?.map((tag, index) => (
            <div
              key={index}
              className="flex items-center space-x-1 bg-primary-900 text-primary-300 px-3 py-1 rounded-full text-sm"
            >
              <span>{tag}</span>
              <button
                onClick={() => handleRemoveTag(tag)}
                className="hover:text-primary-100 transition-colors duration-trading-fast"
              >
                <Icon name="X" size={14} />
              </button>
            </div>
          ))}
          
          {showTagInput ? (
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e?.target?.value)}
                onKeyPress={handleKeyPress}
                placeholder="Add tag..."
                maxLength={20}
                className="px-3 py-1 border border-border rounded-full text-sm bg-background text-text-primary focus:outline-none focus:ring-1 focus:ring-primary-100 focus:border-primary"
                autoFocus
              />
              <button
                onClick={handleAddTag}
                className="text-primary hover:text-primary-300 transition-colors duration-trading-fast"
              >
                <Icon name="Check" size={16} />
              </button>
              <button
                onClick={() => {
                  setShowTagInput(false);
                  setNewTag('');
                }}
                className="text-text-secondary hover:text-text-primary transition-colors duration-trading-fast"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
          ) : marketData?.tags?.length < 5 && (
            <button
              onClick={() => setShowTagInput(true)}
              className="flex items-center space-x-1 bg-surface-hover border border-dashed border-border text-text-secondary px-3 py-1 rounded-full text-sm hover:text-text-primary hover:border-border-hover transition-colors duration-trading-fast"
            >
              <Icon name="Plus" size={14} />
              <span>Add tag</span>
            </button>
          )}
        </div>
        <p className="text-xs text-text-secondary">
          Add up to 5 relevant tags to help users discover your market. Max 20 characters per tag.
        </p>
      </div>

      {/* End Date & Time */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-body-semibold text-text-primary mb-2">
            End Date *
          </label>
          <input
            type="date"
            value={marketData?.endDate}
            onChange={(e) => updateMarketData('endDate', e?.target?.value)}
            min={new Date()?.toISOString()?.split('T')?.[0]}
            className={`w-full px-4 py-3 border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary bg-background text-text-primary ${
              validationErrors?.endDate ? 'border-error' : 'border-border'
            }`}
          />
          {validationErrors?.endDate && (
            <p className="mt-2 text-sm text-error flex items-center">
              <Icon name="AlertCircle" size={16} className="mr-1" />
              {validationErrors?.endDate}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-body-semibold text-text-primary mb-2">
            End Time
          </label>
          <input
            type="time"
            value={marketData?.endTime}
            onChange={(e) => updateMarketData('endTime', e?.target?.value)}
            className="w-full px-4 py-3 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary bg-background text-text-primary"
          />
        </div>
      </div>

      {/* Timezone */}
      <div>
        <label className="block text-sm font-body-semibold text-text-primary mb-2">
          Timezone
        </label>
        <select
          value={marketData?.timezone}
          onChange={(e) => updateMarketData('timezone', e?.target?.value)}
          className="w-full px-4 py-3 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary bg-background text-text-primary"
        >
          <option value="UTC">UTC (Coordinated Universal Time)</option>
          <option value="EST">EST (Eastern Standard Time)</option>
          <option value="PST">PST (Pacific Standard Time)</option>
          <option value="GMT">GMT (Greenwich Mean Time)</option>
          <option value="CET">CET (Central European Time)</option>
        </select>
      </div>
    </div>
  );
};

export default MarketDetailsForm;